import React from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import { IStudentFlow, IStudentSpeaking, IVerbaScholaStudentStep, VerbaScholaApi } from '../api/VerbaScholaApi';
import VerbaScholaStudentLessonStepPage from '../page/VerbaScholaStudentLessonStepPage';
import { VerbaScholaStudentPreviewTransport } from './VerbaScholaStudentAttemptTransport';

/** The real student step player, mounted with a read-only teacher preview transport. */
export default function VerbaScholaTeacherStudentPreview({ stepId, exerciseTitle, studentInstructions, onNext, onPrevious, projectedStep }: {
    stepId: number; exerciseTitle: string; studentInstructions: string | null; onNext?: () => void; onPrevious?: () => void;
    /** A proposed version of the step from a review projection: shown as is, with answers unchecked. */
    projectedStep?: IVerbaScholaStudentStep;
}) {
    const [step, setStep] = React.useState<IVerbaScholaStudentStep | null>(null);
    const [ready, setReady] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [notice, setNotice] = React.useState<string | null>(null);
    const noticeRef = React.useRef<HTMLDivElement>(null);
    const attemptNumber = React.useRef(0);
    React.useEffect(() => { if (notice) noticeRef.current?.focus(); }, [notice]);
    React.useEffect(() => {
        let active = true;
        setStep(null); setReady(false); setError(null); setNotice(null); attemptNumber.current = 0;
        if (projectedStep) { setStep(projectedStep); return () => { active = false; }; }
        void VerbaScholaApi.getTeacherStudentPreview(stepId).then(response => {
            if (active) setStep(response.data);
        }).catch(() => { if (active) setError('The saved activity could not be loaded for preview.'); });
        return () => { active = false; };
    }, [stepId, projectedStep]);
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!step) return <Box role="status" sx={{ textAlign: 'center', py: 5 }}><CircularProgress size={28} aria-label="Loading student preview" /></Box>;
    if (!step.is_supported) return <Alert severity="warning">This legacy activity is not supported by the redesigned student player.</Alert>;
    if (!step.is_playable) return <Alert severity="warning">This saved activity needs more content before it can be played by a student. Finish its fields in Edit, then return to preview.</Alert>;
    const advance = () => {
        if (onNext) onNext();
        else setNotice('End of this exercise preview. Choose another activity above or return to Edit.');
    };
    const onSpeakingUpdate = (speaking: IStudentSpeaking) => setStep(previous => previous ? { ...previous, speaking } : previous);
    const onFlowUpdate = (flow: IStudentFlow) => setStep(previous => previous ? { ...previous, flow } : previous);
    return <VerbaScholaStudentPreviewTransport step={step} unchecked={Boolean(projectedStep)}>
        {projectedStep && <Alert severity="info" sx={{ mb: 2 }}>Proposed version. Answers and recordings are not checked until the change is applied.</Alert>}
        {step.is_hidden && <Alert severity="warning" sx={{ mb: 2 }}>This activity is hidden from students.</Alert>}
        {notice && <Alert ref={noticeRef} tabIndex={-1} role="status" severity="info" sx={{ mb: 2 }}>{notice}</Alert>}
        <VerbaScholaStudentLessonStepPage current={{ exerciseTitle, studentInstructions, step }} preview
            sessionNumber={1} showSessionNumber={false} ready={ready} freshPractice={true} shouldRestartSpeaking={() => false}
            nextAttemptNumber={() => ++attemptNumber.current} onComplete={() => setReady(true)}
            onInstructionComplete={() => { setReady(true); advance(); }} onAdvance={advance} onFlowAdvance={advance}
            onPrevious={onPrevious} advanceLabel={onNext ? 'Next activity' : 'Finish preview'} onExit={() => setNotice('Preview stays on this lesson. Use Edit to return to authoring.')}
            onSpeakingUpdate={onSpeakingUpdate} onFlowUpdate={onFlowUpdate}
            onFlowRefresh={async () => {
                const refreshed = (await VerbaScholaApi.getTeacherStudentPreview(stepId)).data.flow;
                if (!refreshed) throw new Error('This lesson choice is no longer ready for preview.');
                return refreshed;
            }}
            onFlowNavigate={() => setNotice('This destination is shown for review. Preview does not open or save the next lesson.')} />
    </VerbaScholaStudentPreviewTransport>;
}
